import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type { ApiMeta } from '@/lib/http-core';
import type {
  CourierCompany,
  InvoiceBulkJob,
  InvoiceBulkJobDetail,
  InvoiceBulkJobFilter,
  InvoiceBulkUploadResult,
} from '../types/invoiceBulk';

/**
 * ID20 송장 일괄등록 API. (be-order 계약.)
 * 다운로드는 blob, 업로드는 multipart(FormData — Content-Type 미지정으로 boundary 자동),
 * 목록/상세는 envelope { success, data, meta }.
 */
const BASE = '/admin/v1/orders/invoice-bulk';
const COURIERS_BASE = '/admin/v1/orders/invoice-bulk/couriers';

// be-order: jobs/couriers 응답은 data.items[] (단순 배열 아님) + meta.pagination(jobs).
interface JobsEnvelope {
  success: boolean;
  data?: { items?: InvoiceBulkJob[] | null } | null;
  meta?: ApiMeta;
}

interface JobDetailEnvelope {
  success: boolean;
  data?: InvoiceBulkJobDetail | null;
}

interface UploadEnvelope {
  success: boolean;
  data?: InvoiceBulkUploadResult | null;
}

interface CouriersEnvelope {
  success: boolean;
  data?: { items?: CourierCompany[] | null } | null;
}

export interface InvoiceBulkJobsPage {
  items: InvoiceBulkJob[];
  pagination: ApiMeta['pagination'] | undefined;
}

const buildJobsQuery = (filter: InvoiceBulkJobFilter, page: number, size: number): string => {
  const qs = new URLSearchParams();
  // wire 는 0-based, 목록 UI 는 1-based.
  qs.set('page', String(Math.max(0, page - 1)));
  qs.set('size', String(size));
  if (filter.supplierScope) qs.set('supplierScope', filter.supplierScope);
  if (filter.keyword.trim()) qs.set('keyword', filter.keyword.trim());
  if (filter.status) qs.set('status', filter.status);
  if (filter.dateFrom) qs.set('dateFrom', filter.dateFrom);
  if (filter.dateTo) qs.set('dateTo', filter.dateTo);
  return qs.toString();
};

export const invoiceBulkJobsQueryKey = (filter: InvoiceBulkJobFilter, page: number, size: number) =>
  ['admin', 'invoice-bulk-jobs', filter, page, size] as const;

export const invoiceBulkJobQueryKey = (jobId: string) =>
  ['admin', 'invoice-bulk-job', jobId] as const;

export const couriersQueryKey = () => ['admin', 'couriers'] as const;

export const useInvoiceBulkJobsQuery = (
  filter: InvoiceBulkJobFilter,
  page: number,
  size: number,
) =>
  useQuery<InvoiceBulkJobsPage>({
    queryKey: invoiceBulkJobsQueryKey(filter, page, size),
    queryFn: async () => {
      const res = await adminFetch(`${BASE}/jobs?${buildJobsQuery(filter, page, size)}`);
      if (!res.ok) throw new Error(`invoice bulk jobs failed: ${res.status}`);
      const env = (await res.json()) as JobsEnvelope;
      return { items: env.data?.items ?? [], pagination: env.meta?.pagination };
    },
  });

export const useInvoiceBulkJobQuery = (jobId: string | null) =>
  useQuery<InvoiceBulkJobDetail>({
    queryKey: invoiceBulkJobQueryKey(jobId ?? ''),
    queryFn: async () => {
      const res = await adminFetch(`${BASE}/jobs/${jobId}`);
      if (!res.ok) throw new Error(`invoice bulk job detail failed: ${res.status}`);
      const env = (await res.json()) as JobDetailEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    enabled: jobId != null,
  });

export const useCouriersQuery = () =>
  useQuery<CourierCompany[]>({
    queryKey: couriersQueryKey(),
    queryFn: async () => {
      const res = await adminFetch(COURIERS_BASE);
      if (!res.ok) throw new Error(`couriers failed: ${res.status}`);
      const env = (await res.json()) as CouriersEnvelope;
      return env.data?.items ?? [];
    },
    // 마스터 데이터 — 자주 안 바뀜.
    staleTime: 5 * 60 * 1000,
  });

/** 배송대기 주문 prefill 템플릿(xlsx) 다운로드. blob → a[download]. */
export const downloadInvoiceTemplate = async (
  supplierScope: string,
): Promise<{ blob: Blob; filename: string }> => {
  const qs = supplierScope ? `?supplierScope=${encodeURIComponent(supplierScope)}` : '';
  const res = await adminFetch(`${BASE}/template${qs}`);
  if (!res.ok) throw new Error(`invoice template download failed: ${res.status}`);
  const blob = await res.blob();
  // Content-Disposition 의 filename 을 우선 사용, 없으면 날짜 기반 fallback.
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
  const filename = match?.[1] ? decodeURIComponent(match[1]) : defaultTemplateName();
  return { blob, filename };
};

const defaultTemplateName = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `invoice_bulk_template_${y}${m}${d}.xlsx`;
};

export const useInvoiceBulkUploadMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      supplierScope,
    }: {
      file: File;
      supplierScope: string;
    }): Promise<InvoiceBulkUploadResult> => {
      const form = new FormData();
      form.append('file', file);
      if (supplierScope) form.append('supplierScope', supplierScope);

      // multipart: Content-Type 을 직접 넣지 않아야 브라우저가 boundary 를 붙인다 —
      // body 가 FormData 면 adminFetch 가 빼주므로 호출부에서 헤더를 손댈 필요가 없다.
      const res = await adminFetch(BASE, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) throw new Error(`invoice bulk upload failed: ${res.status}`);
      const env = (await res.json()) as UploadEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'invoice-bulk-jobs'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
  });
};
